import React, { useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "../vendor/DayJs";

import { CURRENT_LOCALE_CODE_KEY, CURRENT_LOCALE_KEY } from "../../StorageKeys";
import * as StorageUtils from "./StorageUtils";
import * as NavigationUtils from "./NavigationUtils";
import * as Utils from "./Utils";
import { isFiveKeysTheme } from "./ThemeUtils";
import { ModulePossiblyDefault } from "../types/UtilTypes";
import { useQueryParams } from "../hook/NavigationHooks";
import { useOnceImmediate } from "../hook/MiscHooks";
import { Translatable } from "./Translatable";
import localisationData from "../data/localisation-data.json";

const USE_CACHE = true || process.env.NODE_ENV === "production";
// Russian uses functions for some date formatting which don't get stored properly
// For now, don't store specified dayjs values in local storage
const NON_CACHED_LOCALES = ["ru", "he", "de", "pl", "hu", "cs", "bg", "sk"];
const STRINGS_VERSION = localisationData.strings_version;

export interface LocaleOptions {
	technical?: boolean;
	dir?: "rtl" | "ltr";
	fullSupport?: boolean;
}

export const locale = (
	code: string,
	name: string,
	loader: () => Promise<LocaleData> | LocaleData,
	options?: LocaleOptions,
): LocaleMetadata => {
	return {
		code: code,
		name: name,
		loader: loader,
		options: options || {},
	};
};

export const createLocaleMap = (
	locales: LocaleMetadata[],
): Record<string, LocaleMetadata> => {
	return locales.reduce((loaderMap, locale) => {
		loaderMap[locale.code] = locale;
		return loaderMap;
	}, {} as Record<string, LocaleMetadata>);
};

const locales = require("../locale").default;

export type TextElement = Translatable | string | number;

export interface LocaleMetadata {
	name: string;
	code: string;
	loader: () =>
		| Promise<ModulePossiblyDefault<LocaleData>>
		| ModulePossiblyDefault<LocaleData>;
	options: LocaleOptions;
}

export interface LocaleData {
	dateCode?: string;
	extend?: string;
	strings: Record<string, string>;
}

export type Locale = LocaleData & Omit<LocaleMetadata, "loader">;

export interface LocaleState {
	locale: Locale | undefined;
	code: string | undefined;
	dateCode: string | undefined;
}

export interface LocaleContextState extends LocaleState {
	setCode: (code: string) => void;
	key: number;
}

const createLocale = (
	localeMetadata: LocaleMetadata,
	localeData: LocaleData,
	base: LocaleData | null,
): Locale => {
	return {
		...Utils.deepMerge(base || {}, localeData),
		...localeMetadata,
	};
};

export const DEFAULT_LOCALE: Locale = createLocale(
	locales["en-gb"],
	locales["en-gb"].loader() as LocaleData,
	locales["en"].loader() as LocaleData,
);

const localeState: LocaleState = {
	dateCode: window.navigator.language,
	code: StorageUtils.get(CURRENT_LOCALE_CODE_KEY, window.navigator.language),
	locale: USE_CACHE
		? StorageUtils.get(CURRENT_LOCALE_KEY, DEFAULT_LOCALE)
		: DEFAULT_LOCALE,
};

export const getState = () => localeState;

export const LocaleContext = React.createContext<LocaleContextState>({
	locale: DEFAULT_LOCALE,
	dateCode: (dayjs.locale as any)(),
	code: DEFAULT_LOCALE.code,
	setCode: () => {},
	key: -1,
});

const isFiveKeys = isFiveKeysTheme();

const localeMap: Record<string, Locale | null> = {
	[DEFAULT_LOCALE.code]: DEFAULT_LOCALE,
};

const loadLocale = async (code: string): Promise<Locale | undefined> => {
	if (code === null) {
		return undefined;
	}

	// if it's already loaded or we already know it doesn't exist simply return it or the default
	let locale: Locale | undefined | null = localeMap[code];
	if (locale !== undefined) {
		return locale || DEFAULT_LOCALE;
	}

	const rootLocaleDataKey = isFiveKeys
		? "FIVE_KEYS_LOCALE_DATA"
		: "LOCALE_DATA";

	const localeKey = StorageUtils.createStorageKey(
		`${rootLocaleDataKey}[v${STRINGS_VERSION}, ${code}]`,
		null,
		{
			expiry: Math.abs(dayjs().add(2, "month").diff(Date.now())),
		},
	);
	const cachedLocale = USE_CACHE && StorageUtils.get(localeKey);
	if (cachedLocale) {
		return cachedLocale;
	}

	let localeData: LocaleData | null = null;

	if (code) {
		const localeMetadata = locales[code];
		if (localeMetadata) {
			const localeDataModule = await localeMetadata.loader();

			localeData = localeDataModule.default
				? localeDataModule.default
				: localeDataModule;
			const base = localeData?.extend
				? await loadLocale(localeData.extend)
				: null;
			locale = createLocale(
				localeMetadata,
				localeData as LocaleData,
				base as any,
			);
		}
	}
	localeMap[code] = locale || null;
	StorageUtils.set(localeKey, locale);
	return locale;
};

const loadDayJSLocale = async (locale: Locale | null): Promise<void> => {
	const dateCode = locale
		? locale.dateCode || locale.code.substr(0, 5)
		: "en-gb";

	if (!dayjs.Ls[dateCode]) {
		const dayJsLocaleKey = StorageUtils.createStorageKey(
			`DAYJS_LOCALE_DATA[${dateCode}]`,
			null,
			{
				expiry: Math.abs(dayjs().add(2, "month").diff(Date.now())),
			},
		);
		const cachedDayJsLocale =
			USE_CACHE &&
			!NON_CACHED_LOCALES.includes(dateCode) &&
			StorageUtils.get(dayJsLocaleKey);
		if (cachedDayJsLocale) {
			(window as any)[`day_js_${dateCode}`] = cachedDayJsLocale;
			dayjs.locale(cachedDayJsLocale);
		} else {
			try {
				window.dayjs = dayjs;
				await import(`dayjs/locale/${dateCode}.js`);
				delete window.dayjs;
				StorageUtils.set(dayJsLocaleKey, dayjs.Ls[dateCode]);
			} catch (error) {
				console.error(`Failed to load dayjs locale '${dateCode}'`, error);
			}
		}
	}
	// eslint-disable-next-line
	console.debug(`Set date locale to '${dateCode}'`);
	dayjs.locale({
		...(dayjs.Ls[dateCode] || dayjs.Ls[DEFAULT_LOCALE.code]),
		weekStart: 1,
	});
};

let initializedLocale = false;

export const LocaleProvider = (props: React.PropsWithChildren<{}>) => {
	const [contextKey, setContextKey] = useState(0);

	const beginLoadLocale = useCallback(async (): Promise<void> => {
		const { code } = localeState;

		try {
			// for debug purposes we want to be able to have no locale
			const locale = code ? await loadLocale(code) : undefined;
			localeState.locale = locale;
			StorageUtils.set(CURRENT_LOCALE_KEY, locale);
			await loadDayJSLocale(locale || DEFAULT_LOCALE);
			setContextKey((contextKey) => contextKey + 1);
			document.documentElement.lang = locale?.code || DEFAULT_LOCALE.code;
			document.documentElement.dir = locale?.options?.dir || "ltr";
		} catch (error) {
			console.error("Loading locale failed!", error);
		}
	}, []);

	const setCode = useCallback(
		(code: string) => {
			let validCode = code;
			if (code !== null && !locales[code]) {
				const firstPart = code.split("-")[0];
				if (locales[firstPart]) {
					validCode = firstPart;
				} else {
					validCode = DEFAULT_LOCALE.code;
				}
				console.error(
					`Invalid locale '${validCode}', falling back to '${validCode}'`,
				);
			}
			if (!initializedLocale || localeState.code !== validCode) {
				initializedLocale = true;
				// This line has to be above the beginLoadLocale call, to ensure the selected locale is loaded
				localeState.code = validCode;
				StorageUtils.set(CURRENT_LOCALE_CODE_KEY, validCode);
				NavigationUtils.block("load_locale_data", beginLoadLocale());
			}
		},
		[beginLoadLocale],
	);

	const [{ locale }] = useQueryParams<{ locale: string }>(["locale"]);
	useEffect(() => {
		if (locale && localeState.code !== locale.toLowerCase()) {
			setCode(locale.toLowerCase());
		}
		// eslint-disable-next-line
	}, [locale]);

	// must be called immediately in order to block nav
	useOnceImmediate(() => {
		setCode(locale || localeState.code || DEFAULT_LOCALE.code);
	});

	const dateCode = (dayjs.locale as any)();

	const { children } = props;

	const contextState = useMemo(
		() => ({
			...localeState,
			key: contextKey,
			dateCode: dateCode,
			setCode: setCode,
		}),
		// eslint-disable-next-line
		[dateCode, localeState.code],
	);

	return (
		<LocaleContext.Provider value={contextState}>
			{children}
		</LocaleContext.Provider>
	);
};

export const translate = (
	text: TextElement,
	locale: Locale | null | undefined,
): string => {
	if (!text) return text as any;
	if (
		text instanceof Translatable ||
		(typeof text === "string" && text.startsWith("%"))
	) {
		const translatable = Translatable.of(text);
		try {
			if (translatable && locale === null) return translatable.toFallback();
			const string =
				locale &&
				translatable.process((translatable) =>
					translatableToText(translatable, locale),
				);
			return string === undefined || string === null
				? translatable.toFallback()
				: string;
		} catch (error) {
			console.error(`Translating string '${translatable.id}' failed:`, error);
			return translatable.toFallback();
		}
	}
	return text as any;
};

const translatableToText = (
	translatable: Translatable,
	locale: Locale | null | undefined,
): string => {
	let string =
		locale?.strings[translatable.id] || DEFAULT_LOCALE.strings[translatable.id];
	if (typeof string === "string") {
		for (const key of Object.keys(translatable.vars)) {
			const value = translatable.vars[key];
			if (value !== undefined && value !== null) {
				string = string.replace(
					new RegExp(`{{${key}}}`, "g"),
					translate(value, locale),
				);
			}
		}
	}
	return string;
};

export const getValueForLocale = (
	locale: string,
	values: any,
	key?: string,
) => {
	const localeCode = locale.toLowerCase();
	const fullLocaleValue =
		values[localeCode] ||
		(key && values.find((val: any) => val[key]?.toLowerCase() === localeCode));

	if (fullLocaleValue) {
		return fullLocaleValue;
	}

	const shortCode = localeCode.split("-")[0];
	const shortLocaleValue =
		values[shortCode] ||
		(key && values.find((val: any) => val[key]?.toLowerCase() === shortCode));

	if (shortLocaleValue) {
		return shortLocaleValue;
	}

	return null;
};
